import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Select, { Creatable } from 'react-select';
import escapeStringRegexp from 'escape-string-regexp';

import { arrowRenderer, clearRenderer, promptTextCreator } from '@lightspeed/cirrus-autocomplete';

export const TOP_BAR_HEIGHT = 66;

const isOptionUnique = ({ option, options }) => {
  const matchRegex = new RegExp(`^${escapeStringRegexp(option.label)}$`, 'i');
  const match = options.filter(
    item => typeof item.label === 'string' && item.label.search(matchRegex) !== -1
  );
  return match.length === 0;
};

const customPromptTextCreator = label =>
  promptTextCreator(
    <span>
      Add <var>&ldquo;{label}&rdquo;</var>
    </span>
  );

const renderOption = option => {
  const { label, translate } = option;
  const optionCls = cn('needsclick', {
    notranslate: !translate,
  });
  if (option.className && option.className === 'Select-create-option-placeholder') {
    return <div className="needsclick react-autocomplete">{label}</div>;
  }
  return <div className={optionCls}>{label}</div>;
};

const valueRenderer = value => (!value.translate ? <var>{value.label}</var> : value.label);

const isTouch = 'ontouchstart' in document.documentElement;

class Autocomplete extends Component {
  constructor(props) {
    super(props);
    this.autocompleteRef = React.createRef();
    this.handleFocus = this.handleFocus.bind(this);
  }

  handleFocus() {
    // On touch devices, we want to scroll to the top of the input
    // when focusing, else in most cases the keyboard will overlay
    // the dropdown menu.
    const { id } = this.props.inputProps;
    if (id && isTouch) {
      const elementPosition = this.autocompleteRef.current.getBoundingClientRect();
      window.scroll({
        top: elementPosition.top + window.scrollY - TOP_BAR_HEIGHT,
      });
    }
  }

  render() {
    const {
      name,
      value,
      options,
      tabIndex,
      inputProps,
      placeholder,
      disabled,
      creatable,
      clearable,
      searchable,
      isLoading,
      isError,
      onChange,
      onInputChange,
      onNewOptionClick,
    } = this.props;

    const SelectComponent = creatable ? Creatable : Select;
    const inputName = `${name}_autocomplete`;

    const className = cn('cr-autocomplete', {
      'has-error': isError,
    });

    return (
      <div ref={this.autocompleteRef}>
        <SelectComponent
          name={inputName}
          value={value}
          className={className}
          isLoading={isLoading}
          options={options}
          tabIndex={tabIndex}
          inputProps={inputProps}
          placeholder={placeholder}
          disabled={disabled}
          autoBlur={isTouch}
          escapeClearsValue={false}
          clearable={clearable}
          searchable={searchable}
          autosize={false}
          matchProp="label"
          simpleValue
          onChange={onChange}
          onInputChange={onInputChange}
          onFocus={this.handleFocus}
          onNewOptionClick={onNewOptionClick}
          isOptionUnique={isOptionUnique}
          optionRenderer={renderOption}
          arrowRenderer={arrowRenderer}
          clearRenderer={clearRenderer}
          valueRenderer={valueRenderer}
          promptTextCreator={creatable ? customPromptTextCreator : undefined}
          showNewOptionAtTop={false}
        />
        <input id={name} name={name} type="hidden" className="view_view" readOnly value={value} />
      </div>
    );
  }
}

Autocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  inputProps: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    maxLength: PropTypes.number,
  }),
  tabIndex: PropTypes.string,
  creatable: PropTypes.bool,
  clearable: PropTypes.bool,
  searchable: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onNewOptionClick: PropTypes.func,
};

Autocomplete.defaultProps = {
  value: '',
  options: [],
  inputProps: {},
  tabIndex: '0',
  creatable: false,
  searchable: true,
  placeholder: 'Please select',
};

export default Autocomplete;
